import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './BugReport.css';

const BugReport: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    description: '',
    system: '',
    version: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Using Formspree for email handling
      const response = await fetch('https://formspree.io/f/movlnbjn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: `Bug Report: ${formData.subject}`,
          description: formData.description,
          system: formData.system || 'Not specified',
          version: formData.version || 'Not specified',
          _subject: `Bug Report: ${formData.subject}`,
          _replyto: formData.email
        })
      });

      if (response.ok) {
        posthog.capture('bug_report_submitted', { os: formData.system, app_version: formData.version });
        setSubmitSuccess(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          description: '',
          system: '',
          version: ''
        });
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      console.error('Form submission failed:', error);
      posthog.captureException(error);
      setSubmitError('Failed to send bug report. Please try again or contact us directly at support@codec422.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bug-report-page">
      <Header showNav={true} />
      {/* Hero Section */}
      <section className="bug-report-hero">
        <div className="container">
          <div className="bug-report-hero-content">
            <h1>Report a Bug</h1>
            <p>Found an issue? Let us know and we'll fix it!</p>
          </div>
        </div>
      </section>

      {/* Bug Report Form */}
      <section className="bug-report-content">
        <div className="container">
          <div className="bug-report-form-container">
            {submitSuccess ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h2>Thank you for your bug report!</h2>
                <p>Your bug report has been sent to our support team. We'll review it and get back to you soon.</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => setSubmitSuccess(false)}
                >
                  Submit Another Report
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bug-report-form">
                {submitError && (
                  <div className="error-message">
                    {submitError}
                  </div>
                )}
                
                <div className="form-section">
                  <h2>Contact Information</h2>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">Name *</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h2>Bug Details</h2>
                  <div className="form-group">
                    <label htmlFor="subject">What's the issue? *</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="Brief description of the problem"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="description">Tell us more about the bug *</label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe what happened and what you were trying to do..."
                      rows={4}
                      required
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h2>System Information (Optional)</h2>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="system">Operating System</label>
                      <select
                        id="system"
                        name="system"
                        value={formData.system}
                        onChange={handleInputChange}
                      >
                        <option value="">Select your OS</option>
                        <option value="macOS">macOS</option>
                        <option value="Windows">Windows</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="version">
                        ColorCache Version
                        <span className="tooltip-icon" data-tooltip="The version number is visible at the bottom of the ColorCache extension">ⓘ</span>
                      </label>
                      <input
                        type="text"
                        id="version"
                        name="version"
                        value={formData.version}
                        onChange={handleInputChange}
                        placeholder="e.g., 1.0.0"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Submit Bug Report'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BugReport; 